import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";
import { formatIstSqlDateTime } from "../../utils/datetime";
import Payment from "../../Payment/Payment";
import { continueAfterGateway } from "../../utils/paymentResult";

const HV_BASES = (ApiBaseUrl) => [
  `${ApiBaseUrl}/customer/home-visit`,
  `${ApiBaseUrl}/api/home-visit`,
];

async function hvPost(ApiBaseUrl, path, body) {
  let lastErr;
  for (const base of HV_BASES(ApiBaseUrl)) {
    try {
      const r = await axios.post(`${base}${path}`, body);
      if (r.data?.Result === true || r.data?.ResponseCode === 200) return r.data;
      lastErr = r.data;
    } catch (e) {
      lastErr = e?.response?.data || e;
    }
  }
  return lastErr;
}

export default function HomeVisitRequest({ onClose }) {
  const { ApiBaseUrl, setIsLoginShow, paymentStatus, setPaymentStatus, transactionid } = useContext(MyContext);
  const navigate = useNavigate();
  const Userid = secureLocalStorage.getItem("Userid");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [members, setMembers] = useState([]);
  const [familyMemId, setFamilyMemId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);

  const closeModal = () => {
    document.body.style.overflow = "auto";
    if (onClose) onClose();
    else navigate("/");
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  useEffect(() => {
    if (!Userid) {
      setIsLoginShow(true);
      return;
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLat(String(pos.coords.latitude));
          setLon(String(pos.coords.longitude));
        },
        () => {}
      );
    }
    axios
      .post(`${ApiBaseUrl}/customer/home`, { id: Userid, lat: "0", lon: "0" })
      .then((r) => setMembers(r.data.family_member || []))
      .catch(() => {});

    hvPost(ApiBaseUrl, "/quote", { id: Userid }).then((d) => {
      if (d?.quote) setQuote(d.quote);
    });

    const cached = sessionStorage.getItem("user_address");
    if (cached) setAddress(cached);
  }, [Userid, ApiBaseUrl, setIsLoginShow]);

  useEffect(() => {
    if (paymentStatus !== "PaymentDone" && paymentStatus !== "PaymentFaild") return;
    setPaymentStatus("");
    if (paymentStatus === "PaymentFaild") {
      continueAfterGateway(navigate, { success: false, cancelled: true, transactionId: "0" });
      return;
    }
    continueAfterGateway(navigate, { success: true, transactionId: transactionid || "" });
  }, [paymentStatus, navigate, transactionid, setPaymentStatus]);

  const submit = async () => {
    if (!Userid) {
      setIsLoginShow(true);
      return;
    }
    if (!address.trim()) {
      showToast({ title: "Enter your address", id: "error" });
      return;
    }
    setSubmitting(true);
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const body = {
      id: Userid,
      service_code: "HV_GENERAL",
      schedule_start: formatIstSqlDateTime(now),
      schedule_end: formatIstSqlDateTime(end),
      address_json: { line1: address.trim() },
      patient_lat: Number(lat) || 0,
      patient_lon: Number(lon) || 0,
      payment_mode: "online",
      tot_price: quote?.tot_price,
    };
    if (notes.trim()) body.notes = notes.trim();
    if (familyMemId) body.family_mem_id = familyMemId;

    const data = await hvPost(ApiBaseUrl, "/bookings", body);
    setSubmitting(false);
    if (data?.Result === true || data?.ResponseCode === 200) {
      const booking = {
        id: data.booking_id,
        booking_id: data.booking_id,
        tot_price: data.tot_price || quote?.tot_price || 0,
      };
      sessionStorage.setItem("page", "7");
      sessionStorage.setItem(
        "PaymentData",
        JSON.stringify({
          booking_id: booking.id,
          customer_id: Userid,
          amount: booking.tot_price,
          payment_mode: "online",
        })
      );
      setPendingBooking(booking);
      setShowPay(true);
    } else {
      showToast({
        title: data?.message || "Home visit is not available yet. Try Home Care instead.",
        id: "error",
      });
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={closeModal}
    >
      <div
        className="bg-white w-full sm:max-w-xl sm:rounded-[20px] rounded-t-[20px] max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white z-10 flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <p className="text-xs uppercase tracking-wide text-rm-primary font-semibold">Home visit</p>
            <h1 className="text-xl font-bold text-rm-ink">Book a doctor at home</h1>
            <p className="text-sm text-gray-600 mt-1">
              A qualified doctor can visit you at the address below.
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="shrink-0 bg-gray-100 hover:bg-gray-200 rounded-full p-2"
            aria-label="Close"
          >
            <IoMdClose className="text-[22px]" />
          </button>
        </div>

        <div className="px-5 py-5">
          {members.length > 0 && (
            <>
              <label className="block text-sm font-medium mb-1">Patient</label>
              <select
                className="w-full border rounded-lg px-3 py-2 mb-4"
                value={familyMemId}
                onChange={(e) => setFamilyMemId(e.target.value)}
              >
                <option value="">Self (account holder)</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <label className="block text-sm font-medium mb-1">Address</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 mb-4 min-h-[90px]"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Full address"
          />

          <label className="block text-sm font-medium mb-1">Notes (optional)</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 mb-4 min-h-[80px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Symptoms, preferences…"
          />

          {quote?.tot_price != null && (
            <p className="mb-4 text-sm font-semibold text-rm-ink">
              Visit fee: ₹{quote.tot_price}
            </p>
          )}

          {!showPay ? (
            <button
              type="button"
              disabled={submitting}
              className="w-full py-3 rounded-xl bg-[#0d9488] text-white font-semibold disabled:opacity-60"
              onClick={submit}
            >
              {submitting ? "Submitting…" : "Continue to pay"}
            </button>
          ) : (
            <div className="mt-2">
              <p className="text-sm text-gray-600 mb-3">Complete payment to notify nearby doctors.</p>
              <Payment
                Amount={pendingBooking?.tot_price || quote?.tot_price || 0}
                Data={{
                  booking_id: pendingBooking?.id || pendingBooking?.booking_id,
                  customer_id: Userid,
                  amount: pendingBooking?.tot_price || quote?.tot_price || 0,
                  payment_mode: "online",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
